import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import pagosService from "../../../../services/pagosService";
import { listarConceptos } from "../../../../services/pagoConceptosService";
import { listarMetodos } from "../../../../services/pagoMetodosService";
import { buscarMatriculas, obtenerMatricula } from "../../../../services/matriculasService";
import { listarCargosPorMatriculas } from "../../../../services/cargosAdicionalesService";
import { showToast } from "../../../../utils/showToast";
import { listarCuentasBancarias } from "../../../../services/contificoService";

const initialForm = {
  matricula_id: "",
  monto: "",
  recargo_porcentaje: "",
  metodo_id: "",
  metodo_nombre: "",
  concepto_id: "",
  concepto_nombre: "",
  cargo_id: null,
  cargo_ids: [],
  monto_total_cobro: null,
  seleccion_items: [],
  _lockMontoTotal: null,
  fecha_pago: "",
  cuenta_bancaria_contifico_id: "",
  facturar_tercero: false,
  responsable_tipo_doc: "cedula",
  responsable_documento: "",
  responsable_nombres: "",
  responsable_apellidos: "",
  responsable_email: "",
  responsable_direccion: "",
};

const buildPrefilledForm = (prefill = null) => {
  if (!prefill) return { ...initialForm };
  return {
    ...initialForm,
    ...prefill,
    matricula_id: prefill.matricula_id != null ? String(prefill.matricula_id) : "",
    monto: prefill.monto != null && prefill.monto !== "" ? String(prefill.monto) : "",
    concepto_id: prefill.concepto_id != null ? String(prefill.concepto_id) : "",
    concepto_nombre: "",
    cargo_id: prefill.cargo_id ?? null,
    cargo_ids: Array.isArray(prefill.cargo_ids) ? prefill.cargo_ids : [],
    monto_total_cobro: prefill.monto_total_cobro ?? null,
    seleccion_items: Array.isArray(prefill.seleccion_items) ? prefill.seleccion_items : [],
    _lockMontoTotal: (() => {
      const items = Array.isArray(prefill.seleccion_items) ? prefill.seleccion_items : [];
      const hasMatricula = items.some((it) => it.type === "matricula");
      const hasCargo = items.some((it) => it.type === "cargo");
      if (hasMatricula && hasCargo) return prefill.monto_total_cobro ?? null;
      return null;
    })(),
  };
};

export default function usePagosForm({ onCreated }) {
  const [form, setForm] = useState(initialForm);
  const [modal, setModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lastCreatedAt, setLastCreatedAt] = useState(null);
  const [resultadoPago, setResultadoPago] = useState(null);
  const [matriculasOptions, setMatriculasOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [payableItems, setPayableItems] = useState([]);
  const [matriculaInfo, setMatriculaInfo] = useState(null);
  const [loadingMatriculaInfo, setLoadingMatriculaInfo] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [restrictEstudianteId, setRestrictEstudianteId] = useState(null);
  const [lockMatricula, setLockMatricula] = useState(false);
  const [lockMonto, setLockMonto] = useState(false);
  const [prefillMode, setPrefillMode] = useState(false); // true = viene desde DeudaDetailPanel con ítems pre-seleccionados
  const [conceptos, setConceptos] = useState([]);
  const conceptosRef = useRef([]);
  const [metodos, setMetodos] = useState([]);
  const [cuentasBancarias, setCuentasBancarias] = useState([]);

  useEffect(() => {
    listarConceptos()
      .then((data) => {
        const lista = Array.isArray(data) ? data : [];
        conceptosRef.current = lista;
        setConceptos(lista);
        setForm((prev) => ({
          ...prev,
          concepto_id: prev.concepto_id || (lista[0]?.id ? String(lista[0].id) : ""),
        }));
      })
      .catch(() => {});
    listarMetodos()
      .then((data) => {
        // "Bonificación" no es seleccionable manualmente — se aplica solo desde el módulo de bonificaciones
        const lista = (Array.isArray(data) ? data : []).filter((m) => m.valor !== 'bonificacion');
        setMetodos(lista);
        setForm((prev) => ({
          ...prev,
          metodo_id: prev.metodo_id || (lista[0]?.id ? String(lista[0].id) : ""),
        }));
      })
      .catch(() => {});

    listarCuentasBancarias()
      .then((data) => setCuentasBancarias(Array.isArray(data) ? data : []))
      .catch(() => { /* Contífico no configurado o sin conexión */ });
  }, []);

  const resetForm = useCallback(() => setForm((prev) => ({
    ...initialForm,
    concepto_id: prev.concepto_id || "",
  })), []);

  const loadMatriculas = useCallback(async ({ search = "", estudianteId = null } = {}) => {
    setLoadingOptions(true);
    try {
      const data = await buscarMatriculas({
        search: search || undefined,
        estudiante_id: estudianteId || undefined,
        limit: 30,
      });
      const options = (Array.isArray(data) ? data : []).map((item) => {
        const nombre = [item.estudiante_nombres, item.estudiante_apellidos].filter(Boolean).join(" ").trim();
        const documento =
          item.estudiante_documento || item.documento_identidad || item.documentoIdentidad || item.documento || "";
        const codigo = item.codigo_matricula || item.codigoMatricula || "";
        const promocion = item.promocion || item.curso || "";
        const saldo = Number(item.saldo ?? 0);
        const estudianteId_ = item.estudiante_id || null;
        return {
          value: item.id,
          label: `${nombre || "Estudiante"}${documento ? ` | ${documento}` : ""} (Codigo ${codigo || `#${item.id}`})`,
          nombre,
          documento,
          codigo,
          promocion,
          saldo,
          estudianteId: estudianteId_,
          estadoPago: item.estado_pago || item.estadoPago || "",
        };
      });
      setMatriculasOptions(options);
      if (!lockMatricula) {
        setForm((prev) => {
          if (!prev.matricula_id) return prev;
          const hasSelection = options.some((opt) => String(opt.value) === String(prev.matricula_id));
          return hasSelection ? prev : { ...prev, matricula_id: "" };
        });
      }

      // Construir payableItems: matrícula + cargos adicionales pendientes
      if (options.length > 0) {
        const items = [];

        // Filas de matrícula con saldo pendiente
        for (const opt of options) {
          if (opt.saldo > 0 || opt.estadoPago === "pendiente" || opt.estadoPago === "parcial") {
            items.push({
              type: "matricula",
              key: `mat-${opt.value}`,
              matricula_id: opt.value,
              estudiante_id: opt.estudianteId,
              estudiante: opt.nombre,
              documento: opt.documento,
              codigo: opt.codigo,
              promocion: opt.promocion,
              monto: opt.saldo,
              concepto_id: null,
              cargo_id: null,
              label: opt.promocion ? `Matrícula — ${opt.promocion}` : "Matrícula",
              lockMonto: false,
            });
          }
        }

        // Cargos adicionales pendientes
        try {
          const ids = options.map((o) => o.value);
          const cargosData = await listarCargosPorMatriculas(ids);
          const cargos = Array.isArray(cargosData?.rows) ? cargosData.rows : [];
          for (const c of cargos) {
            items.push({
              type: "cargo",
              key: `cargo-${c.id}`,
              matricula_id: c.matricula_id,
              estudiante_id: c.estudiante_id,
              estudiante: c.estudiante_nombre,
              documento: c.estudiante_documento,
              codigo: c.codigo_matricula,
              monto: Number(c.monto),
              concepto_id: c.concepto_id,
              cargo_id: c.id,
              label: c.concepto_nombre + (c.observaciones ? ` — ${c.observaciones}` : ""),
              lockMonto: true,
            });
          }
        } catch {
          // no bloquear si falla la carga de cargos
        }

        setPayableItems(items);
      } else {
        setPayableItems([]);
      }
    } catch (err) {
      console.error(err);
      showToast({ type: "danger", message: "No se pudieron cargar las matriculas." });
    } finally {
      setLoadingOptions(false);
    }
  }, [lockMatricula]);

  const loadMatriculaInfo = useCallback(async (id) => {
    if (!id) {
      setMatriculaInfo(null);
      return;
    }
    setLoadingMatriculaInfo(true);
    try {
      const data = await obtenerMatricula(id);
      if (data) {
        const estudiante = [data.estudiante_nombres, data.estudiante_apellidos].filter(Boolean).join(" ").trim();
        setMatriculaInfo({
          estudiante: estudiante || "Estudiante",
          promocion: data.promocion || "Promocion",
          estado_pago: data.estado_pago || data.estadoPago || "pendiente",
          estado_matricula: data.estado_matricula || data.estadoMatricula || "activa",
        });
      } else {
        setMatriculaInfo(null);
      }
    } catch (err) {
      console.error(err);
      setMatriculaInfo(null);
    } finally {
      setLoadingMatriculaInfo(false);
    }
  }, []);

  useEffect(() => {
    if (!modal) return;
    if (!searchTerm.trim() && !restrictEstudianteId && !form.matricula_id) {
      setMatriculasOptions([]);
      setPayableItems([]);
    }
  }, [modal, searchTerm, restrictEstudianteId, form.matricula_id]);

  useEffect(() => {
    if (!modal) return undefined;
    const term = searchTerm.trim();
    if (!term) return undefined;
    const esNumerico = /^\d+$/.test(term);
    const minChars = esNumerico ? 5 : 3;
    if (term.length < minChars) return undefined;

    let activo = true;
    const handler = setTimeout(() => {
      if (!activo) return;
      loadMatriculas({ search: term, estudianteId: restrictEstudianteId });
    }, 350);

    return () => {
      activo = false;
      clearTimeout(handler);
    };
  }, [modal, searchTerm, restrictEstudianteId, loadMatriculas]);

  const openModal = useCallback(
    (prefill = null) => {
      const estudianteId = prefill?.estudiante_id ? Number(prefill.estudiante_id) : null;
      const hasPrefillItems = Array.isArray(prefill?.seleccion_items) && prefill.seleccion_items.length > 0;
      setForm(buildPrefilledForm(prefill));
      setRestrictEstudianteId(estudianteId);
      setLockMatricula(Boolean(prefill?.lockMatricula));
      setLockMonto(Boolean(prefill?.lockMonto));
      setPrefillMode(hasPrefillItems);
      setSearchTerm("");
      setMatriculasOptions([]);
      setPayableItems([]);
      setMatriculaInfo(null);
      setModal(true);
      if (estudianteId) {
        loadMatriculas({ estudianteId });
      }
    },
    [loadMatriculas]
  );
  const closeModal = useCallback(() => {
    setModal(false);
    resetForm();
    setSearchTerm("");
    setMatriculasOptions([]);
    setPayableItems([]);
    setRestrictEstudianteId(null);
    setLockMatricula(false);
    setLockMonto(false);
    setPrefillMode(false);
    setResultadoPago(null);
  }, [resetForm]);

  const onChange = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSelectItem = useCallback((item, { replace = false } = {}) => {
    setForm((prev) => {
      const existingItems = Array.isArray(prev.seleccion_items) ? prev.seleccion_items : [];

      // Modo replace (búsqueda normal): selección simple, reemplaza todo
      if (replace) {
        const alreadySelected = existingItems.length === 1 && existingItems[0].key === item.key;
        if (alreadySelected) {
          // Deseleccionar si se clickea el mismo
          return {
            ...prev,
            matricula_id: "",
            monto: "",
            monto_total_cobro: null,
            concepto_nombre: "",
            cargo_id: null,
            cargo_ids: [],
            seleccion_items: [],
            _lockMontoTotal: null,
          };
        }
        // Reemplazar con el nuevo ítem
        const conceptoMatricula = item.type === "matricula"
          ? (conceptosRef.current.find((c) => c.afecta_matricula === 1) || conceptosRef.current[0])
          : null;
        return {
          ...prev,
          matricula_id: String(item.matricula_id),
          monto: String(Number(item.monto || 0).toFixed(2)),
          monto_total_cobro: Number(item.monto || 0),
          concepto_id: conceptoMatricula ? String(conceptoMatricula.id) : item.concepto_id ? String(item.concepto_id) : prev.concepto_id,
          concepto_nombre: "",
          cargo_id: item.type === "cargo" ? item.cargo_id : null,
          cargo_ids: item.type === "cargo" && item.cargo_id ? [item.cargo_id] : [],
          seleccion_items: [item],
          _lockMontoTotal: null,
        };
      }

      // Modo toggle (prefilled desde DeudaDetailPanel): multi-selección
      // Si ya hay ítems de otra matrícula, resetear y empezar con este ítem
      const sameMatricula = existingItems.every(
        (it) => String(it.matricula_id) === String(item.matricula_id)
      );
      const baseItems = sameMatricula ? existingItems : [];

      const alreadySelected = baseItems.some((it) => it.key === item.key);

      // Toggle: si ya está, quitar; si no está, agregar
      const newItems = alreadySelected
        ? baseItems.filter((it) => it.key !== item.key)
        : [...baseItems, item];

      // Si quedó sin ítems, limpiar selección conservando método y concepto
      if (newItems.length === 0) {
        return {
          ...prev,
          matricula_id: "",
          monto: "",
          monto_total_cobro: null,
          concepto_nombre: "",
          cargo_id: null,
          cargo_ids: [],
          seleccion_items: [],
          _lockMontoTotal: null,
        };
      }

      const matriculaItem = newItems.find((it) => it.type === "matricula");
      const cargoItems = newItems.filter((it) => it.type === "cargo" && it.cargo_id);
      const cargoIds = cargoItems.map((it) => it.cargo_id);

      const total = newItems.reduce((sum, it) => sum + Number(it.monto || 0), 0);
      const hasCombined = matriculaItem && cargoIds.length > 0;

      // Determinar concepto:
      // - matrícula (sola o combinada): usar el concepto con afecta_matricula=1
      // - solo cargos: tomar el concepto del primer cargo
      let nextConceptoId = prev.concepto_id;
      if (matriculaItem) {
        const conceptoMatricula = conceptosRef.current.find((c) => c.afecta_matricula === 1) || conceptosRef.current[0];
        nextConceptoId = conceptoMatricula ? String(conceptoMatricula.id) : prev.concepto_id;
      } else if (cargoItems.length > 0) {
        nextConceptoId = cargoItems[0].concepto_id ? String(cargoItems[0].concepto_id) : prev.concepto_id;
      }

      return {
        ...prev,
        matricula_id: String(newItems[0].matricula_id),
        monto: String(total.toFixed(2)),
        monto_total_cobro: total,
        concepto_id: nextConceptoId,
        concepto_nombre: "",
        cargo_id: cargoIds.length === 1 ? cargoIds[0] : null,
        cargo_ids: cargoIds,
        seleccion_items: newItems,
        _lockMontoTotal: hasCombined ? total : null,
      };
    });
    setSearchTerm(item.estudiante || item.documento || "");
  }, []);

  useEffect(() => {
    if (!modal) return;
    if (!form.matricula_id) {
      setMatriculaInfo(null);
      return;
    }
    loadMatriculaInfo(form.matricula_id);
  }, [form.matricula_id, modal, loadMatriculaInfo]);

  // Cuando la matrícula viene pre-fijada (lockMatricula o selección programática),
  // poblar el searchTerm con el nombre del estudiante en cuanto matriculaInfo llegue.
  useEffect(() => {
    if (!modal || !form.matricula_id || !matriculaInfo?.estudiante) return;
    setSearchTerm((prev) => prev || matriculaInfo.estudiante);
  }, [modal, form.matricula_id, matriculaInfo?.estudiante]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedMetodo = useMemo(
    () => metodos.find((m) => String(m.id) === String(form.metodo_id)),
    [metodos, form.metodo_id]
  );

  // Auto-rellenar porcentaje_recargo cuando cambia el método de pago
  // y limpiar cuenta bancaria si el método no requiere transferencia
  useEffect(() => {
    if (!modal) return;
    if (!selectedMetodo) return;
    const pct = Number(selectedMetodo.porcentaje_recargo ?? 0);
    const esTra = selectedMetodo.valor === 'transferencia' || selectedMetodo.valor === 'deposito';
    setForm((prev) => ({
      ...prev,
      recargo_porcentaje: pct > 0 ? String(pct) : "",
      ...(!esTra ? { cuenta_bancaria_contifico_id: "" } : {}),
    }));
  }, [selectedMetodo, modal]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = useCallback(async () => {
    if (submitting) return;
    const monto = parseFloat(form.monto);
    const matriculaId = Number(form.matricula_id);
    if (!form.matricula_id) {
      showToast({ type: "warning", message: "Selecciona un codigo de matricula." });
      return;
    }
    if (Number.isNaN(monto) || monto <= 0) {
      showToast({ type: "warning", message: "Ingresa un monto valido." });
      return;
    }

    const esTra = selectedMetodo?.valor === 'transferencia' || selectedMetodo?.valor === 'deposito';
    if (esTra && cuentasBancarias.length > 0 && !form.cuenta_bancaria_contifico_id) {
      showToast({ type: "warning", message: "Selecciona la cuenta bancaria a la que realizó la transferencia." });
      return;
    }

    setSubmitting(true);
    try {
      const conceptoPayload = form.concepto_id
        ? { concepto_id: Number(form.concepto_id) }
        : { concepto_nombre: form.concepto_nombre || "Matrícula" };

      const metodoPayload = form.metodo_id
        ? { metodo_id: Number(form.metodo_id) }
        : { metodo_nombre: form.metodo_nombre || "Efectivo" };

      const recargoPct = parseFloat(form.recargo_porcentaje) || 0;
      const recargo_monto = recargoPct > 0 ? parseFloat((monto * recargoPct / 100).toFixed(2)) : 0;

      const facturadoPayload = form.facturar_tercero && form.responsable_documento?.trim()
        ? {
            responsable_nombre:    [form.responsable_nombres?.trim(), form.responsable_apellidos?.trim()].filter(Boolean).join(" ") || "",
            responsable_documento: form.responsable_documento?.trim() || "",
            responsable_tipo_doc:  form.responsable_tipo_doc          || "cedula",
            responsable_email:     form.responsable_email?.trim()     || "",
            responsable_direccion: form.responsable_direccion?.trim() || "",
          }
        : {};

      const result = await pagosService.crear({
        matricula_id: matriculaId,
        monto,
        recargo_monto,
        ...metodoPayload,
        ...conceptoPayload,
        ...(form.cargo_id ? { cargo_id: Number(form.cargo_id) } : {}),
        ...(form.cargo_ids?.length ? { cargo_ids: form.cargo_ids.map(Number) } : {}),
        ...(form.fecha_pago ? { fecha_pago: form.fecha_pago } : {}),
        ...(form.cuenta_bancaria_contifico_id ? { cuenta_bancaria_contifico_id: form.cuenta_bancaria_contifico_id } : {}),
        ...facturadoPayload,
      });

      const cambio = Number(result?.cambio ?? 0);
      const totalPagado = Number(result?.total_pagado ?? result?.totalPagado ?? 0);
      const precioFinal = Number(result?.precio_final ?? result?.precioFinal ?? 0);
      const pendiente = Math.max(0, precioFinal - totalPagado);
      const resumen = [
        cambio > 0 ? `Cambio: $${cambio.toFixed(2)}` : null,
        precioFinal ? `Pendiente: $${pendiente.toFixed(2)}` : null,
      ]
        .filter(Boolean)
        .join(" | ");

      showToast({
        type: "success",
        message:
          (selectedMetodo ? `Pago registrado (${selectedMetodo.nombre}).` : "Pago registrado.") +
          (resumen ? ` ${resumen}` : ""),
      });

      onCreated?.(result);
      setLastCreatedAt(Date.now());
      setResultadoPago({
        pagoId: result.pago_id,
        pagoIdCargos: result.pago_id_cargos || null,
        cambio: Number(result?.cambio ?? 0),
        matriculaId: form.matricula_id || null,
      });
    } catch (err) {
      console.error(err);
      const message = err?.response?.data?.message || err.message || "No se pudo registrar el pago.";
      showToast({ type: "danger", message });
    } finally {
      setSubmitting(false);
    }
  }, [form, selectedMetodo, onCreated, submitting, cuentasBancarias.length]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    form,
    modal,
    openModal,
    closeModal,
    submitting,
    onChange,
    submit,
    matriculasOptions,
    loadingOptions,
    reloadOptions: () => {
      const term = searchTerm.trim();
      if (!term && !restrictEstudianteId) {
        setMatriculasOptions([]);
        setPayableItems([]);
        return;
      }
      loadMatriculas({
        search: term,
        estudianteId: restrictEstudianteId,
      });
    },
    matriculaInfo,
    loadingMatriculaInfo,
    searchTerm,
    setSearchTerm,
    lockMatricula,
    lockMonto,
    lastCreatedAt,
    conceptos,
    metodos,
    payableItems,
    handleSelectItem,
    prefillMode,
    resultadoPago,
    selectedMetodo,
    cuentasBancarias,
  };
}
